import { jsPDF } from 'jspdf';
import { Order } from '../types/admin';

interface StoreInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  taxRate: number;
}

const defaultStoreInfo: StoreInfo = {
  name: 'T-RaX Car Care',
  address: 'Yeni Birlik Sanayi Sitesi, Saray, 675. Cadde No:24/12, 06980 Kahramankazan/Ankara',
  phone: '0312 815 11 65',
  email: 'info@sbtkimya.com.tr',
  taxRate: 20,
};

export function generateInvoicePDF(order: Order, storeInfo?: Partial<StoreInfo>): Blob {
  const store = { ...defaultStoreInfo, ...storeInfo };
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  // Colors
  const brandRed: [number, number, number] = [227, 30, 36];
  const darkGray: [number, number, number] = [51, 51, 51];
  const lightGray: [number, number, number] = [153, 153, 153];
  const veryLightGray: [number, number, number] = [245, 245, 245];

  // === HEADER ===
  // Red header bar
  doc.setFillColor(...brandRed);
  doc.rect(0, 0, pageWidth, 35, 'F');

  // Company name
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(store.name, margin, 18);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(store.address, margin, 25);
  doc.text(`${store.phone} | ${store.email}`, margin, 30);

  // Invoice badge
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageWidth - margin - 55, 8, 55, 20, 3, 3, 'F');
  doc.setTextColor(...brandRed);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('FATURA', pageWidth - margin - 45, 20);
  doc.setTextColor(...lightGray);
  doc.setFontSize(7);
  doc.text('FATURA NO', pageWidth - margin - 50, 28);
  doc.setTextColor(...darkGray);
  doc.setFontSize(8);
  doc.text(order.orderNumber, pageWidth - margin - 20, 28);

  // === INVOICE META ===
  let y = 45;

  doc.setTextColor(...darkGray);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');

  // Left column - Seller
  doc.text('SATICI BİLGİLERİ', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(store.name, margin, y); y += 5;
  doc.text(store.address, margin, y); y += 5;
  doc.text(store.phone, margin, y); y += 5;
  doc.text(store.email, margin, y);

  // Right column - Buyer
  let ry = 45;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const rightX = pageWidth / 2 + 10;
  doc.text('ALICI BİLGİLERİ', rightX, ry);
  ry += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(order.customerName, rightX, ry); ry += 5;
  doc.text(order.customerEmail, rightX, ry); ry += 5;
  doc.text(order.customerPhone, rightX, ry); ry += 5;
  doc.text(order.shippingAddress, rightX, ry); ry += 5;
  doc.text(`${order.district}/${order.city} ${order.postalCode}`, rightX, ry);

  // Invoice details
  y = Math.max(y, ry) + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('FATURA DETAYLARI', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Fatura Tarihi: ${new Date(order.createdAt).toLocaleDateString('tr-TR')}`, margin, y);
  doc.text(`Ödeme Yöntemi: ${
    order.paymentMethod === 'credit_card' ? 'Kredi Kartı' :
    order.paymentMethod === 'bank_transfer' ? 'Havale/EFT' : 'Kapıda Ödeme'
  }`, rightX, y);
  y += 5;
  doc.text(`Ödeme Durumu: ${
    order.paymentStatus === 'paid' ? 'Ödendi' :
    order.paymentStatus === 'pending' ? 'Bekliyor' :
    order.paymentStatus === 'failed' ? 'Başarısız' : 'İade'
  }`, rightX, y);

  y += 10;

  // === TABLE HEADER ===
  doc.setFillColor(...veryLightGray);
  doc.roundedRect(margin, y, contentWidth, 8, 1, 1, 'F');
  y += 6;

  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('SIRA', margin + 3, y);
  doc.text('ÜRÜN ADI', margin + 15, y);
  doc.text('BİRİM FİYAT', margin + 110, y);
  doc.text('MİKTAR', margin + 140, y);
  doc.text('KDV', margin + 155, y);
  doc.text('TOPLAM', margin + 175, y);

  y += 6;

  // === TABLE ROWS ===
  doc.setTextColor(...darkGray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  order.items.forEach((item, index) => {
    if (y > pageHeight - 60) {
      // Add new page if needed
      doc.addPage();
      y = margin;
    }

    const lineTotal = item.product.salePrice * item.quantity;
    const taxAmount = lineTotal * (store.taxRate / 100);

    // Alternate row background
    if (index % 2 === 0) {
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, y - 4, contentWidth, 8, 'F');
    }

    doc.text(`${index + 1}`, margin + 5, y);
    
    // Truncate long product names
    const maxNameWidth = 90;
    let productName = item.product.name;
    while (doc.getTextWidth(productName) > maxNameWidth && productName.length > 10) {
      productName = productName.slice(0, -4) + '...';
    }
    doc.text(productName, margin + 15, y);

    doc.text(`₺${item.product.salePrice.toLocaleString('tr-TR')}`, margin + 110, y);
    doc.text(`${item.quantity}`, margin + 145, y);
    doc.text(`%${store.taxRate}`, margin + 155, y);
    doc.setTextColor(...brandRed);
    doc.setFont('helvetica', 'bold');
    doc.text(`₺${lineTotal.toLocaleString('tr-TR')}`, margin + 175, y);
    doc.setTextColor(...darkGray);
    doc.setFont('helvetica', 'normal');

    y += 8;
  });

  // Separator line
  y += 2;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // === TOTALS ===
  const subtotal = order.items.reduce((sum, item) => sum + item.product.salePrice * item.quantity, 0);
  const taxTotal = subtotal * (store.taxRate / 100);
  const grandTotal = order.totalAmount;
  const shipping = order.shippingCost;

  const totalsX = pageWidth - margin - 70;
  const valuesX = pageWidth - margin - 3;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');

  doc.text('Ara Toplam:', totalsX, y);
  doc.text(`₺${subtotal.toLocaleString('tr-TR')}`, valuesX, y);
  y += 6;

  doc.text(`KDV (%${store.taxRate}):`, totalsX, y);
  doc.text(`₺${taxTotal.toLocaleString('tr-TR')}`, valuesX, y);
  y += 6;

  if (shipping > 0) {
    doc.text('Kargo:', totalsX, y);
    doc.text(`₺${shipping.toLocaleString('tr-TR')}`, valuesX, y);
    y += 6;
  }

  y += 2;
  doc.setFillColor(...brandRed);
  doc.roundedRect(totalsX - 5, y - 5, 78, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('GENEL TOPLAM:', totalsX, y + 2);
  doc.text(`₺${grandTotal.toLocaleString('tr-TR')}`, valuesX, y + 2);

  // === FOOTER ===
  y = pageHeight - 30;
  doc.setTextColor(...lightGray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Bu fatura elektronik ortamda oluşturulmuştur.', margin, y);
  doc.text(`${store.name} | ${store.phone} | ${store.email}`, margin, y + 5);
  doc.text(`Oluşturulma Tarihi: ${new Date().toLocaleString('tr-TR')}`, margin, y + 10);

  // Stamp area
  doc.setDrawColor(200, 200, 200);
  doc.setLineDashPattern([2, 2], 0);
  doc.roundedRect(pageWidth - margin - 60, y - 5, 60, 20, 2, 2, 'S');
  doc.setFontSize(7);
  doc.setTextColor(...lightGray);
  doc.text('KAŞE / İMZA', pageWidth - margin - 43, y + 8);

  return doc.output('blob');
}

export function downloadInvoice(order: Order, storeInfo?: Partial<StoreInfo>): void {
  const blob = generateInvoicePDF(order, storeInfo);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `FATURA-${order.orderNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
